import React, { useState, useEffect } from 'react';
import { Table, message, Select, Modal, Button, Popconfirm } from 'antd';
import { adminInstance } from './api/axiosConfig';

const { Option } = Select;

const SavedOutputs = () => {
  const [outputs, setOutputs] = useState([]);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedOutput, setSelectedOutput] = useState(null);
  const [tableColumns, setTableColumns] = useState([]);

  useEffect(() => {
    fetchTables();
  }, []);

  useEffect(() => {
    if (selectedTable) {
      fetchOutputs(selectedTable);
      fetchTableStructure(selectedTable);
    }
  }, [selectedTable]);

  const fetchTables = async () => {
    try {
      const response = await adminInstance.get('/tables');
      setTables(response.data);
      if (response.data.length > 0) {
        setSelectedTable(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
      message.error('Failed to fetch tables');
    }
  };

  const fetchOutputs = async (table) => {
    setLoading(true);
    try {
      const response = await adminInstance.get(`/outputs?table=${table}`);
      setOutputs(response.data);
    } catch (error) {
      console.error('Error fetching outputs:', error);
      message.error('Failed to fetch outputs');
    }
    setLoading(false);
  };

  const fetchTableStructure = async (table) => {
    try {
      const response = await adminInstance.get(`/table-structure?table=${table}`);
      const columns = response.data.map(column => ({
        title: column.name,
        dataIndex: column.name,
        key: column.name,
      }));
      columns.push({
        title: 'Action',
        key: 'action',
        render: (_, record) => (
          <span>
            <Button onClick={() => {
              setSelectedOutput(record);
              setModalVisible(true);
            }}>
              View
            </Button>
            <Popconfirm
              title="Are you sure you want to delete this entry?"
              onConfirm={() => handleDeleteEntry(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button style={{ marginLeft: 8 }} danger>Delete</Button>
            </Popconfirm>
          </span>
        ),
      });
      setTableColumns(columns);
    } catch (error) {
      console.error('Error fetching table structure:', error);
      message.error('Failed to fetch table structure');
    }
  };

  const handleExport = async () => {
    try {
      const response = await adminInstance.get(`/export-outputs?table=${selectedTable}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedTable}_outputs.csv`);
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      console.error('Error exporting data:', error);
      message.error('Failed to export data');
    }
  };

  const handleDeleteTable = async () => {
    try {
      await adminInstance.delete(`/delete-table?table=${selectedTable}`);
      message.success('Table deleted successfully');
      fetchTables();
      setSelectedTable('');
      setOutputs([]);
    } catch (error) {
      console.error('Error deleting table:', error);
      message.error('Failed to delete table');
    }
  };

  const handleDeleteEntry = async (id) => {
    try {
      await adminInstance.delete(`/delete-entry?table=${selectedTable}&id=${id}`);
      message.success('Entry deleted successfully');
      fetchOutputs(selectedTable);
    } catch (error) {
      console.error('Error deleting entry:', error);
      message.error('Failed to delete entry');
    }
  };

  return (
    <div>
      <h1>Saved Outputs</h1>
      <Select
        style={{ width: 200, marginBottom: 16 }}
        value={selectedTable}
        onChange={setSelectedTable}
        placeholder="Select a table"
      >
        {tables.map(table => (
          <Option key={table} value={table}>{table}</Option>
        ))}
      </Select>
      <Button onClick={handleExport} style={{ marginLeft: 16, marginBottom: 16 }}>
        Export CSV
      </Button>
      <Popconfirm
        title="Are you sure you want to delete this table? This action cannot be undone."
        onConfirm={handleDeleteTable}
        okText="Yes"
        cancelText="No"
      >
        <Button danger style={{ marginLeft: 16, marginBottom: 16 }}>
          Delete Table
        </Button>
      </Popconfirm>
      <Table 
        columns={tableColumns} 
        dataSource={outputs} 
        rowKey="id"
        loading={loading}
      />
      <Modal
        title="Output Details"
        visible={modalVisible}
        onOk={() => setModalVisible(false)}
        onCancel={() => setModalVisible(false)}
        width={800}
      >
        {selectedOutput && (
          <pre style={{
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            {JSON.stringify(selectedOutput, null, 2)}
          </pre>
        )}
      </Modal>
    </div>
  );
};

export default SavedOutputs;